import { Color, Spacing } from '@persona-id/design-tokens';
import {
  ArrowCollapseIcon,
  ArrowExpandIcon,
  LinkExternalIcon,
  PlusIcon,
  TagIcon,
  TransferOutIcon,
  UsersCheckIcon,
} from '@persona-id/design-tokens/icons';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { StringParam, useQueryParams } from 'use-query-params';

import GraphCanvasV2 from 'dashboard/components/GraphCanvasV2';
import CanvasViewportChangeEvent from 'dashboard/components/GraphCanvasV2/events/canvas-viewport-change-event';
import G6Wrapper from 'dashboard/components/GraphCanvasV2/G6Wrapper';
import GraphExplorerPanel from 'dashboard/components/GraphExplorerPanel/GraphExplorerPanel';
import GraphExplorerQueryProgressBar from 'dashboard/components/GraphExplorerQueryProgressBar';
import GraphExportModal from 'dashboard/components/GraphExportModal';
import GraphExportModalV2 from 'dashboard/components/GraphExportModalV2';
import GraphHoverCard from 'dashboard/components/GraphHoverCard';
import { useBackend } from 'dashboard/components/GraphModule/hooks/useBackend';
import { useEventBus } from 'dashboard/components/GraphModule/hooks/useEventBus';
import { useG6Wrapper } from 'dashboard/components/GraphModule/hooks/useG6Wrapper';
import { useGraphData } from 'dashboard/components/GraphModule/hooks/useGraphData';
import { useGraphExplorer } from 'dashboard/components/GraphModule/hooks/useGraphExplorer';
import { useGraphQuerying } from 'dashboard/components/GraphModule/hooks/useGraphQuerying';
import { useGraphQueryLoadingState } from 'dashboard/components/GraphModule/hooks/useGraphQueryLoadingState';
import GraphToolbar from 'dashboard/components/GraphToolbar';
import LoadingOverlay from 'dashboard/components/LoadingOverlay';
import TagsModal from 'dashboard/components/TagsModal';
import Tooltip from 'dashboard/components/Tooltip';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AccountType } from 'dashboard/models/account-type';
import { DatumTypeToken, Node, NodeId } from 'dashboard/models/graph';
import { Backend, ServerNodeFunctionsConfig } from 'dashboard/models/graph-backend';
import { DemoBackend } from 'dashboard/models/graph-demo-backend';
import { GraphExplorer, GraphExplorerQueryLoadingStage } from 'dashboard/models/graph-explorer';
import { GraphQueryBody } from 'dashboard/models/graph-query';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { GraphSnapshot } from 'dashboard/models/graph-snapshot';
import { List } from 'dashboard/models/list';
import { showMessage } from 'dashboard/redux/actions/messages';
import { Z_INDICES } from 'dashboard/styles/variables';
import AccountStatusModal from 'dashboard/views/DashboardGraphExplorerView/AccountStatusModal';
import AddToListModal from 'dashboard/views/DashboardGraphExplorerView/AddToListModal';
import GraphCanvasMinimap from 'dashboard/views/DashboardGraphExplorerView/GraphCanvasMinimap';
import GraphExplorerHeader from 'dashboard/views/DashboardGraphExplorerView/GraphExplorerHeader';
import Button from 'design-system/components/Button';
import { SegmentClient } from 'lib/clients/SegmentClient';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import {
  ACCOUNT_DATUM_TYPE,
  BROWSER_FINGERPRINT_DATUM_TYPE,
  DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE,
  EMAIL_DATUM_TYPE,
  getNodeLimit,
  GOVERNMENT_ID_DATUM_TYPE,
  NAME_DATUM_TYPE,
  NETWORK_DATUM_TYPE,
  PHONE_NUMBER_DATUM_TYPE,
} from 'lib/constants/graph';
import { LoadFn } from 'lib/hooks/useApiCallback';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

// A position relative container allows us to use absolute
// positioning to compose elements into this container
const PositionRelativeContainer = styled.div`
  display: flex;
  border-left: 1px solid ${Color.CoreSlate100};
  position: relative;
`;

const CanvasContainer = styled.div`
  width: 100%;
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
  overflow: hidden;
`;

const LayoutLifter = styled.div`
  position: absolute;
  right: 0;
`;

const PositionedGraphExplorerPanel = styled(GraphExplorerPanel)`
  position: fixed;
  z-index: ${Z_INDICES.floatingBar - 1};
  right: 0;
`;

const StyledLoadingOverlay = styled(LoadingOverlay)`
  background: rgba(255, 255, 255, 0.25);
`;

const StyledTooltip = styled(Tooltip)`
  z-index: ${Z_INDICES.floatingBar};
  position: relative;
`;

const StyledIconButton = styled(Button)`
  margin-right: ${Spacing[2]};
`;

const ActionButton = styled(Button)`
  display: inline-block;
`;

const MIN_CANVAS_HEIGHT = 600;
const MIN_CANVAS_WIDTH = 600;

// TODO(kerry): Fix this code, once we decouple account IDs from node IDs.
const nodeIdToAccountId = (nodeId: NodeId): string | null => {
  const { token, value } = nodeId;

  if (token === ACCOUNT_DATUM_TYPE.token) {
    return value;
  }

  return null;
};

const ELIGIBLE_DATUM_TYPE_TOKENS_FOR_LISTS = [
  GOVERNMENT_ID_DATUM_TYPE.token,
  NETWORK_DATUM_TYPE.token,
  NAME_DATUM_TYPE.token,
  PHONE_NUMBER_DATUM_TYPE.token,
  EMAIL_DATUM_TYPE.token,
  BROWSER_FINGERPRINT_DATUM_TYPE.token,
  DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token,
];

interface DashboardGraphExplorerViewProps {
  accountTypes: AccountType[];
  nodeFunctionsConfigs: ServerNodeFunctionsConfig[];
  backendRef: React.MutableRefObject<Backend>;
  clearGraphQueryTemplate: () => void;
  g6WrapperRef: React.MutableRefObject<G6Wrapper>;
  graphExplorerRef: React.MutableRefObject<GraphExplorer>;
  graphQueryTemplate: GraphQueryTemplate;
  graphSnapshot: GraphSnapshot | undefined;
  handleAddToList: (
    listId: string,
    datumTypeToken: DatumTypeToken,
    listItemValue: string,
    idClass?: GovernmentIdClass,
  ) => void;
  handleLoadGraphQueryTemplate: (graphQueryTemplateId: string) => Promise<void>;
  handleSaveGraphQueryTemplate: (
    id: string | null,
    name: string,
    description: string | null,
    payload: GraphQueryBody,
    saveAs: boolean,
  ) => Promise<boolean>;
  handleUpdateAccountStatuses: (
    nodeIds: NodeId[],
    accountIds: string[],
    accountStatusId: string,
  ) => void;
  handleCreateGraphSnapshot: LoadFn<
    [graphId: string | undefined, graphQueryId: string, payload: any],
    string | void
  >;
  handleUpdateTags: (nodeIds: NodeId[], accountIds: string[], tags: FormUpdateTagsData) => void;
  lists: List[];
  loadingAccountStatuses: boolean;
  loadingGraphsAndGraph: boolean;
  loadingGraphQueryTemplates: boolean;
  loadingLists: boolean;
  loadingCreateGraphSnapshot: boolean;
  showMessage: typeof showMessage;
  viewWidth: number;
}

const getCanvasHeight = () =>
  Math.max(document.getElementById('canvas-container')?.clientHeight || 0, MIN_CANVAS_HEIGHT);
const getCanvasWidth = () =>
  Math.max(document.getElementById('canvas-container')?.clientWidth || 0, MIN_CANVAS_WIDTH);

const DashboardGraphExplorerView = ({
  accountTypes,
  nodeFunctionsConfigs,
  backendRef,
  clearGraphQueryTemplate,
  g6WrapperRef,
  graphExplorerRef,
  graphQueryTemplate,
  graphSnapshot,
  handleLoadGraphQueryTemplate,
  handleAddToList,
  handleSaveGraphQueryTemplate,
  handleUpdateAccountStatuses,
  handleUpdateTags,
  handleCreateGraphSnapshot,
  lists,
  loadingAccountStatuses,
  loadingGraphsAndGraph,
  loadingGraphQueryTemplates,
  loadingLists,
  loadingCreateGraphSnapshot,
  showMessage,
  viewWidth,
}: DashboardGraphExplorerViewProps) => {
  const shouldShowAccountStatus = useFeatureFlag('dashboard:accounts-show-account-status');
  const useExportV2 = useFeatureFlag('dashboard:graph-use-export-v2');

  // Pass in backendRef to set it
  useBackend(backendRef);
  const {
    graphExplorer,
    exportNodes: exportNodesFromGraphExplorer,
    selectedNodes,
    canZoomIn,
    canZoomOut,
    zoomPercentage,
    selectedViewType,
  } = useGraphExplorer(graphExplorerRef);
  const eventBus = useEventBus();
  const g6Wrapper = useG6Wrapper(g6WrapperRef);
  const backend = useBackend();
  const {
    attemptPollQuery,
    currentGraphQuery,
    handleExpand,
    handleQueryWithClearCanvas: handleQuery,
    handleQueryCancel,
    setCurrentGraphQuery,
    loadFromSnapshot,
    createSnapshotPayload,
  } = useGraphQuerying();
  const [exportNodes, setExportNodes] = useState([]);
  const { queryHint, queryLoadingState, updateQueryLoadingState } = useGraphQueryLoadingState();

  const [canvasDimensions, setCanvasDimensions] = useState({ width: null, height: null });
  const [exportModal, { enable: showExportModal, disable: hideExportModal }] = useToggle(false);
  const [tagsModal, { enable: showTagsModal, disable: hideTagsModal }] = useToggle(false);
  const [nodesToTag, setNodesToTag] = useState<NodeId[]>([]);
  const [isListModalShown, { enable: showListModal, disable: hideListModal }] = useToggle(false);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [nodeForList, setNodeForList] = useState<NodeId>(null);
  const [
    graphQueryEditorVisible,
    { toggle: graphQueryEditorToggle, enable: graphQueryEditorShow, disable: graphQueryEditorHide },
  ] = useToggle(true);
  const [
    displayPanelExpanded,
    { enable: displayPanelExpand, disable: _displayPanelCollapse, toggle: displayPanelToggle },
  ] = useToggle(false);
  const [
    shouldShowAccountStatusModal,
    { enable: showAccountStatusModal, disable: hideAccountStatusModal },
  ] = useToggle(false);
  const [urlQueryParams, setUrlQueryParams] = useQueryParams({
    queryId: StringParam,
    queryTemplateId: StringParam,
  });
  const isDemoBackend = backend instanceof DemoBackend;

  // -----------------------------------------------------------------------------------------
  // Zoom logic
  // -----------------------------------------------------------------------------------------
  const handleZoomInClick = () => {
    graphExplorerRef.current?.zoomIn();
    handleZoomPercentageChange(graphExplorerRef.current?.getZoomPercentage());
  };

  const handleZoomOutClick = () => {
    graphExplorerRef.current?.zoomOut();
    handleZoomPercentageChange(graphExplorerRef.current?.getZoomPercentage());
  };

  const handleZoomPercentageChange = useCallback(
    (zoomPercentage: number) => {
      if (zoomPercentage !== graphExplorerRef.current?.getZoomPercentage()) {
        graphExplorerRef.current?.setZoomPercentage(zoomPercentage);
      }

      if (g6WrapperRef.current?.zoomPercentage() !== zoomPercentage) {
        g6WrapperRef.current?.zoomSetPercentage(zoomPercentage);
      }
    },
    [g6WrapperRef, graphExplorerRef],
  );

  const handleToggleParseView = useCallback(
    (viewType: string) => {
      graphExplorerRef.current?.setViewType(viewType, g6Wrapper);
    },
    [g6WrapperRef, graphExplorerRef],
  );

  const rightPanel = useRef<HTMLDivElement>();

  const handleG6Mount = () => {
    eventBus.initialize(g6Wrapper, window, graphExplorer);
  };

  useEffect(() => {
    if (graphQueryTemplate) {
      if (urlQueryParams.queryTemplateId !== graphQueryTemplate.id) {
        setUrlQueryParams({ queryTemplateId: graphQueryTemplate.id });
      }
    } else {
      setUrlQueryParams({ queryTemplateId: undefined });
    }

    const tryLoadQuery = async () => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (await attemptPollQuery(urlQueryParams.queryId, true)) {
        displayPanelExpand();
        graphQueryEditorHide();
      } else {
        graphQueryEditorShow();
      }
    };

    // It is very difficult to use the querySentCounter to verify that we should continue the query
    // but this should not be a problem so long as LoadingGraph stage is never set more than once
    // which is reasonable for now, as the explorer does not need to load graph more than
    if (
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      queryLoadingState.stage === GraphExplorerQueryLoadingStage.LoadingGraph &&
      urlQueryParams.queryId
    ) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      updateQueryLoadingState(GraphExplorerQueryLoadingStage.FetchingQuery);
      tryLoadQuery();
    }
  }, [urlQueryParams.queryId, urlQueryParams.queryTemplateId, graphQueryTemplate]);

  // Tagging Actions
  const hideTagsModalAndReenableCanvasActions = () => {
    hideTagsModal();
    eventBus.setIsDisabled(false);
  };
  const showTagsModalAndDisableCanvasActions = () => {
    showTagsModal();
    eventBus.setIsDisabled(true);
  };
  const handleUpdateTagsAndClose = (tags: FormUpdateTagsData) => {
    handleUpdateTags(
      nodesToTag,
      nodesToTag.map((nodeToTag) => nodeToTag.value),
      tags,
    );
    hideTagsModalAndReenableCanvasActions();
    setNodesToTag([]);
  };
  const setNodeToTag = (nodeToTag: NodeId) => {
    setNodesToTag([nodeToTag]);
  };

  // Account status action
  const handleAccountStatusModalSubmit = ({ accountStatusId }: { accountStatusId: string }) => {
    const accountIds = selectedNodes.map(nodeIdToAccountId).filter(Boolean);

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    handleUpdateAccountStatuses(selectedNodes, accountIds, accountStatusId);
    hideAccountStatusModal();
  };

  // Add to List action
  const hideListModalAndReenableCanvasActions = useCallback((): void => {
    hideListModal();
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setNodeForList(null);
    eventBus.setIsDisabled(false);
  }, [eventBus, hideListModal, setNodeForList]);

  const hasWriteToListPermission = usePermission(Permission.ListWrite);
  const showListModalAndDisableCanvasActions = useCallback((): void => {
    showListModal();
    eventBus.setIsDisabled(true);
  }, [eventBus, showListModal]);

  const handleAddToListAndClose = useCallback(
    (
      listId: string,
      datumTypeToken: DatumTypeToken,
      listItemValue: string,
      idClass?: GovernmentIdClass,
    ) => {
      SegmentClient.sendSegmentTrackEvent('Graph - Add To List');
      handleAddToList(listId, datumTypeToken, listItemValue, idClass);
      hideListModalAndReenableCanvasActions();
    },
    [hideListModalAndReenableCanvasActions, handleAddToList],
  );

  const handleWindowResize = () => {
    let width = getCanvasWidth();
    if (rightPanel.current) {
      width = width - rightPanel.current.clientWidth;
    }
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setCanvasDimensions({ width, height: getCanvasHeight() });
  };

  useEffect(() => {
    handleWindowResize();
  }, [displayPanelExpanded]);

  const wrappedHandleQuery = async (query: GraphQueryBody) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (await handleQuery(query)) {
      displayPanelExpand();
      graphQueryEditorHide();
    } else {
      // pop the editor back if the query failed
      graphQueryEditorShow();
    }
  };

  // -----------------------------------------------------------------------------------------
  // Side panel callbacks
  // -----------------------------------------------------------------------------------------

  const handleClearGraphQueryAndGraphQueryTemplate = useCallback(() => {
    clearGraphQueryTemplate();
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setCurrentGraphQuery(null);
  }, [setCurrentGraphQuery, clearGraphQueryTemplate]);

  useEffect(handleWindowResize, [viewWidth]);
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    g6Wrapper.setHandleOneHopExpansion(handleExpand);
    g6Wrapper.setHandleZoomPercentageChange(handleZoomPercentageChange);
  }, [g6Wrapper, handleExpand, handleZoomPercentageChange]);

  useEffect(() => {
    g6Wrapper.setHandleCollapsedGroupExpand((groupId) => {
      graphExplorer.removeCollapsedGroup(groupId, g6Wrapper);
    });
  }, [g6Wrapper, graphExplorer]);

  useEffect(() => {
    g6Wrapper.setHandleCollapsedNodeExpand((nodeId) => {
      graphExplorer.removeCollapsedPairwiseNode(nodeId, g6Wrapper);
    });
  }, [g6Wrapper, graphExplorer]);

  const handleExportClick = useCallback(
    (nodeIds: NodeId[]) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setExportNodes(nodeIds);
      showExportModal();
    },
    [showExportModal],
  );

  const handleExplorerExportClick = handleExportClick.bind(undefined, exportNodesFromGraphExplorer);

  // Don't show the progress bar
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const loadingGraphQuery = queryLoadingState.stage !== GraphExplorerQueryLoadingStage.Idle;

  const handleRightPanelToggleClick = (e: MouseEvent<HTMLLIElement>) => {
    displayPanelToggle();
    eventBus?.dispatchEvent(new CanvasViewportChangeEvent(e as unknown as Event, g6Wrapper.canvas));
  };

  const handleSelectionChangeAccountStatusClick = () => {
    showAccountStatusModal();
  };

  const handleSelectionTagClick = () => {
    setNodesToTag(
      selectedNodes.filter((nodeId: NodeId) => nodeId.token === ACCOUNT_DATUM_TYPE.token),
    );
    showTagsModalAndDisableCanvasActions();
  };

  // items to load at the beginning.
  const loadingGraphEnvironment =
    loadingAccountStatuses || loadingGraphsAndGraph || loadingGraphQueryTemplates;

  // Disable the event bus if we're loading.
  // Note: This call is cheap enough to call on every render.
  eventBus.setIsDisabled(
    loadingGraphEnvironment ||
      [
        GraphExplorerQueryLoadingStage.SendingQuery,
        GraphExplorerQueryLoadingStage.WaitingForGraphQueryResult,
        GraphExplorerQueryLoadingStage.LoadingGraph,
        GraphExplorerQueryLoadingStage.FetchingQuery,
        GraphExplorerQueryLoadingStage.HydratingGraphNodes,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ].includes(queryLoadingState.stage),
  );

  const onQueryCancel = () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    handleQueryCancel(() => {
      showMessage?.('Query cancelled', MessageType.Notice);
      graphQueryEditorShow();
    });
  };

  const [hasLoadedSnapshot, setHasLoadedSnapshot] = useState(false);

  useEffect(() => {
    if (g6Wrapper.isMounted && graphSnapshot && !hasLoadedSnapshot && loadFromSnapshot) {
      loadFromSnapshot(graphSnapshot);
      setHasLoadedSnapshot(true);
      graphQueryEditorHide();
      displayPanelExpand();
    }
  }, [
    graphSnapshot,
    graphExplorer,
    hasLoadedSnapshot,
    displayPanelExpand,
    graphQueryEditorHide,
    loadFromSnapshot,
    g6Wrapper.isMounted,
  ]);

  const { graph } = useGraphData();
  const handleSnapshotClick = useCallback(() => {
    if (createSnapshotPayload) {
      return handleCreateGraphSnapshot(
        graph?.id,
        currentGraphQuery ? currentGraphQuery.id : '',
        createSnapshotPayload(),
      );
    }
  }, [graph?.id, currentGraphQuery, handleCreateGraphSnapshot, createSnapshotPayload]);

  return (
    <>
      <GraphExplorerHeader
        handleExportClick={handleExplorerExportClick}
        handleSnapshotClick={handleSnapshotClick}
        loadingCreateGraphSnapshot={loadingCreateGraphSnapshot}
      />
      {loadingGraphEnvironment && <StyledLoadingOverlay />}
      {loadingGraphQuery && (
        <GraphExplorerQueryProgressBar
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          queryLoadingState={queryLoadingState}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          queryHint={queryHint}
          handleQueryCancel={onQueryCancel}
        />
      )}
      <PositionRelativeContainer>
        <CanvasContainer id='canvas-container'>
          {canvasDimensions.width != null && (
            <>
              <GraphCanvasV2
                handleG6Mount={handleG6Mount}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                height={canvasDimensions.height}
                selectedNodes={selectedNodes}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                selectionActions={() => [
                  <StyledTooltip
                    key='tag'
                    placement='bottom'
                    trigger='hover'
                    tooltip='Tag'
                    hideArrow={true}
                  >
                    <ActionButton
                      variant='basic'
                      size='sm'
                      width='icon'
                      type='button'
                      onClick={handleSelectionTagClick}
                      id='graph-canvas__tag-selection-button'
                    >
                      <TagIcon />
                    </ActionButton>
                  </StyledTooltip>,
                  shouldShowAccountStatus && (
                    <StyledTooltip
                      key='change-account-status'
                      placement='bottom'
                      trigger='hover'
                      tooltip='Change Account Status'
                      hideArrow={true}
                    >
                      <ActionButton
                        variant='basic'
                        size='sm'
                        width='icon'
                        type='button'
                        onClick={handleSelectionChangeAccountStatusClick}
                        id='graph-canvas__change-account-status-selection-button'
                      >
                        <UsersCheckIcon />
                      </ActionButton>
                    </StyledTooltip>
                  ),
                  <StyledTooltip
                    key='export'
                    placement='bottom'
                    trigger='hover'
                    tooltip='Export'
                    hideArrow={true}
                  >
                    <ActionButton
                      variant='basic'
                      size='sm'
                      width='icon'
                      type='button'
                      disabled={exportNodes.length === 0}
                      onClick={handleExplorerExportClick}
                      id='graph-canvas__export-button'
                    >
                      <TransferOutIcon />
                    </ActionButton>
                  </StyledTooltip>,
                  <StyledTooltip
                    key='collapse'
                    placement='bottom'
                    trigger='hover'
                    tooltip='Collapse'
                    hideArrow={true}
                  >
                    <ActionButton
                      variant='basic'
                      size='sm'
                      width='icon'
                      type='button'
                      onClick={() => {
                        graphExplorer.addCollapsedGroup(g6Wrapper);
                      }}
                      id='graph-canvas__collapse-button'
                    >
                      <ArrowCollapseIcon />
                    </ActionButton>
                  </StyledTooltip>,
                ]}
                width={canvasDimensions.width}
              />
              <GraphToolbar
                canZoomIn={canZoomIn}
                canZoomOut={canZoomOut}
                clearGraphQueryTemplate={clearGraphQueryTemplate}
                eventBus={eventBus}
                g6Wrapper={g6Wrapper}
                graphQueryBarEnabled={true}
                graphQueryEditorVisible={graphQueryEditorVisible}
                graphQueryEditorToggle={graphQueryEditorToggle}
                graphQueryTemplate={graphQueryTemplate}
                handleClearGraphQueryAndGraphQueryTemplate={
                  handleClearGraphQueryAndGraphQueryTemplate
                }
                handleClickRedo={g6Wrapper.redo.bind(g6Wrapper)}
                handleClickUndo={g6Wrapper.undo.bind(g6Wrapper)}
                handleLoadGraphQueryTemplate={handleLoadGraphQueryTemplate}
                handleRightPanelToggleClick={handleRightPanelToggleClick}
                handleQuery={wrappedHandleQuery}
                handleSaveGraphQueryTemplate={handleSaveGraphQueryTemplate}
                handleZoomInClick={handleZoomInClick}
                handleZoomOutClick={handleZoomOutClick}
                handleZoomPercentageChange={handleZoomPercentageChange}
                isDemoBackend={isDemoBackend}
                loadingGraphEnvironment={
                  loadingAccountStatuses || loadingGraphsAndGraph || loadingGraphQueryTemplates
                }
                nodeLimit={getNodeLimit(currentGraphQuery)}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                queryLoadingState={queryLoadingState}
                rightPanelExpanded={displayPanelExpanded}
                width={canvasDimensions.width}
                zoomPercentage={zoomPercentage}
                selectedViewType={selectedViewType}
                handleToggleParseView={handleToggleParseView}
              />
              <GraphHoverCard
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                canvasHeight={canvasDimensions.height}
                canvasWidth={canvasDimensions.width}
                getNodeRenderConfig={graphExplorer.getNodeRenderConfig}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                actions={(node: Node) =>
                  [
                    <StyledTooltip
                      key='expand'
                      placement='bottom'
                      trigger='hover'
                      tooltip='Show Connections'
                      hideArrow={true}
                    >
                      <StyledIconButton
                        width='icon'
                        variant='basic'
                        type='button'
                        id='graph-account-hover-card__expand-button'
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        onClick={() => handleExpand([node.id])}
                        data-test='graph-account-hover-card__expand-button'
                        disabled={!node.state.nonfetchedEdgeCount || node.state.loading}
                      >
                        <ArrowExpandIcon />
                      </StyledIconButton>
                    </StyledTooltip>,
                    ...(node.id.token === ACCOUNT_DATUM_TYPE.token
                      ? [
                          <StyledTooltip
                            key='open-account'
                            placement='bottom'
                            trigger='hover'
                            tooltip='Open'
                            hideArrow={true}
                          >
                            <StyledIconButton
                              width='icon'
                              variant='basic'
                              type='button'
                              id='graph-account-hover-card__open-link-button'
                              onClick={() => {
                                window.open(`/dashboard/accounts/${node.id.value}`, '_blank');
                              }}
                              data-test='graph-account-hover-card__open-link-button'
                            >
                              <LinkExternalIcon />
                            </StyledIconButton>
                          </StyledTooltip>,
                          <StyledTooltip
                            key='tag'
                            placement='bottom'
                            trigger='hover'
                            tooltip='Tag'
                            hideArrow={true}
                          >
                            <StyledIconButton
                              width='icon'
                              variant='basic'
                              type='button'
                              id='graph-account-hover-card__add-tags-button'
                              onClick={() => {
                                showTagsModalAndDisableCanvasActions();
                                setNodeToTag(node.id);
                              }}
                              data-test='graph-account-hover-card__add-tags-button'
                            >
                              <TagIcon />
                            </StyledIconButton>
                          </StyledTooltip>,
                          <StyledTooltip
                            key='export'
                            placement='bottom'
                            trigger='hover'
                            tooltip='Export'
                            hideArrow={true}
                          >
                            <StyledIconButton
                              width='icon'
                              variant='basic'
                              type='button'
                              id='graph-account-hover-card__export-button'
                              onClick={() => handleExportClick([node.id])}
                              data-test='graph-account-hover-card__export-button'
                            >
                              <TransferOutIcon />
                            </StyledIconButton>
                          </StyledTooltip>,
                        ]
                      : [
                          hasWriteToListPermission &&
                            ELIGIBLE_DATUM_TYPE_TOKENS_FOR_LISTS.includes(node.id.token) && (
                              <StyledTooltip
                                key='add-to-list'
                                placement='bottom'
                                trigger='hover'
                                tooltip='Add to List'
                                hideArrow={true}
                              >
                                <StyledIconButton
                                  width='icon'
                                  variant='basic'
                                  type='button'
                                  id='graph-account-hover-card__add-to-list-button'
                                  onClick={() => {
                                    setNodeForList(node.id);
                                    showListModalAndDisableCanvasActions();
                                  }}
                                  data-test='graph-account-hover-card__add-to-list-button'
                                >
                                  <PlusIcon />
                                </StyledIconButton>
                              </StyledTooltip>
                            ),
                        ]),
                  ].filter(Boolean)
                }
              />
              <GraphCanvasMinimap eventBus={eventBus} g6Wrapper={g6Wrapper} />
            </>
          )}
        </CanvasContainer>
        <LayoutLifter>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          {displayPanelExpanded ? <PositionedGraphExplorerPanel elRef={rightPanel} /> : null}
        </LayoutLifter>
      </PositionRelativeContainer>
      <TagsModal
        tags={[]}
        tagScope='all'
        updateTagsModal={tagsModal}
        hideUpdateTagsModal={hideTagsModalAndReenableCanvasActions}
        handleUpdateTagsAndClose={handleUpdateTagsAndClose}
        isAddTag
      />
      <AccountStatusModal
        isOpen={shouldShowAccountStatusModal}
        handleCloseModal={hideAccountStatusModal}
        handleSubmit={handleAccountStatusModalSubmit}
      />
      {useExportV2 ? (
        <GraphExportModalV2
          exportNodes={exportNodes}
          showGraphExportModal={exportModal}
          hideGraphExportModal={hideExportModal}
          accountTypes={accountTypes}
          nodeFunctionsConfigs={nodeFunctionsConfigs}
        />
      ) : (
        <GraphExportModal
          exportNodes={exportNodes}
          showGraphExportModal={exportModal}
          hideGraphExportModal={hideExportModal}
        />
      )}
      {hasWriteToListPermission && (
        <AddToListModal
          nodeId={nodeForList}
          isModalShown={isListModalShown}
          hideModal={hideListModalAndReenableCanvasActions}
          lists={lists}
          loadingLists={loadingLists}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleAddToListAndClose={handleAddToListAndClose}
        />
      )}
    </>
  );
};

export default DashboardGraphExplorerView;
